import { GenericAbortSignal } from 'axios'
import { API_BASIC_URL } from '../constants/base-urls'
import type {
  IBPJSComponent,
  IBenefitComponent,
  IDeductionComponent,
  IEffectiveRate,
  IEmployeePayrollDetail,
  IEmployeePayrollResult,
  IPPH21,
  IPaginationParam,
  IPaginationResponse,
  IPayrollComponentPolicy,
  IPayrollComponentPolicyPayload,
  IPayrollMatrix,
  IPayrollPeriod,
  IPayrollRequest,
  IPayrollTaxSettings,
} from '../types'
import { createAxiosInstance } from '../utils'

const axios = createAxiosInstance({
  baseURL: API_BASIC_URL,
  withAuth: true,
})

/**
 * Master Data
 */
export const fetchPPH21 = () => {
  return axios.get<{ data: { content: IPPH21[] } }>(`/pph21-category`).then((response) => response.data.data)
}

/**
 * BPJS Component
 *
 */
export const fetchBpjsComponent = (signal?: GenericAbortSignal) => {
  return axios.get<{ data: IBPJSComponent }>(`/bpjs-component`, { signal }).then((response) => response.data.data)
}

export const updateBpjsComponent = (payload: Record<string, any>, signal?: GenericAbortSignal) => {
  return axios.put<{ data: IBPJSComponent }>('/bpjs-component', payload, { signal }).then(({ data }) => data.data)
}

/**
 * Benefit Components
 */
export const fetchBenefitComponents = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IBenefitComponent> }>(`/benefit-component`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchBenefitComponent = (oid: string, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IBenefitComponent }>(`/benefit-component/${oid}`, { signal }).then((response) => response.data.data)
}

export const createBenefitComponent = (payload: Record<string, any>, signal?: GenericAbortSignal) => {
  return axios.post<{ data: IBenefitComponent }>('/benefit-component', payload, { signal }).then(({ data }) => data.data)
}

export const updateBenefitComponent = (oid: string, payload: Record<string, any>, signal?: GenericAbortSignal) => {
  return axios.put<{ data: IBenefitComponent }>(`/benefit-component/${oid}`, payload, { signal }).then(({ data }) => data.data)
}

export const deleteBenefitComponent = (oid: string, signal?: GenericAbortSignal) => {
  return axios.delete(`/benefit-component/${oid}`, { signal }).then(({ data }) => data.data)
}

export const applyBenefitToEmployees = (payload: { componentId: string; employeeIds: string[] }, signal?: GenericAbortSignal) => {
  return axios.post('/benefit-component/apply-to-employees', payload, { signal }).then(({ data }) => data.data)
}

export const removeBenefitFromEmployees = (payload: { componentId: string; employeeIds: string[] }, signal?: GenericAbortSignal) => {
  return axios.post('/benefit-component/remove-from-employees', payload, { signal }).then(({ data }) => data.data)
}

/**
 * Deduction Components
 */
export const fetchDeductionComponents = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IDeductionComponent> }>(`/deduction-component`, { params, signal })
    .then((response) => response.data.data)
}

export const fetchDeductionComponent = (oid: string, signal?: GenericAbortSignal) => {
  return axios.get<{ data: IDeductionComponent }>(`/deduction-component/${oid}`, { signal }).then((response) => response.data.data)
}

export const createDeductionComponent = (payload: Record<string, any>, signal?: GenericAbortSignal) => {
  return axios.post<{ data: IDeductionComponent }>('/deduction-component', payload, { signal }).then(({ data }) => data.data)
}

export const updateDeductionComponent = (oid: string, payload: Record<string, any>, signal?: GenericAbortSignal) => {
  return axios.put<{ data: IDeductionComponent }>(`/deduction-component/${oid}`, payload, { signal }).then(({ data }) => data.data)
}

export const deleteDeductionComponent = (oid: string, signal?: GenericAbortSignal) => {
  return axios.delete(`/deduction-component/${oid}`, { signal }).then(({ data }) => data.data)
}

export const applyDeductionToEmployees = (payload: { componentId: string; employeeIds: string[] }, signal?: GenericAbortSignal) => {
  return axios.post('/deduction-component/apply-to-employees', payload, { signal }).then(({ data }) => data.data)
}

export const removeDeductionFromEmployees = (payload: { componentId: string; employeeIds: string[] }, signal?: GenericAbortSignal) => {
  return axios.post('/deduction-component/remove-from-employees', payload, { signal }).then(({ data }) => data.data)
}

/**
 * Payroll
 */
export const fetchPayrollRequests = (params?: { statusRunner?: 'COMPLETED' | 'FAILED' | 'ON_PROCESS' | 'WAITING' } & IPaginationParam) => {
  return axios.get<{ data: IPaginationResponse<IPayrollRequest> }>('/payroll', { params }).then(({ data }) => data.data)
}

export const fetchPayrollRequest = (oid: string) => {
  return axios.get<{ data: IPayrollRequest }>(`/payroll/${oid}`).then(({ data }) => data.data)
}

export const requestForApproval = (oid: string) => {
  return axios.post<{ data: IPayrollRequest }>(`/payroll/request-approval/${oid}`).then(({ data }) => data.data)
}

export const exportPayrollRequest = (oid: string) => {
  return axios.post(`/payroll/export/${oid}`, null, {
    responseType: 'blob',
    headers: {
      Accept: 'application/octet-stream',
    },
  })
  // return axios.post(`/payroll/export/${oid}`)
}

export const exportPayrollBankRelease = (oid: string) => {
  return axios.post(`/payroll/export-bank-release/${oid}`, null, {
    responseType: 'blob',
    headers: {
      Accept: 'application/octet-stream',
    },
  })
  // return axios.post(`/payroll/export/${oid}`)
}

export const updatePayrollRequestStatus = (oid: string, payload: Record<string, any>) => {
  return axios.patch<{ data: IPayrollRequest }>(`/payroll/status/${oid}`, payload).then(({ data }) => data.data)
}

export const createPayrollRequest = (payload: Record<string, any>) => {
  return axios.post<{ data: IPayrollRequest }>('/payroll', payload).then(({ data }) => data.data)
}

export const fetchPayrollRequestResults = (oid: string, params?: IPaginationParam) => {
  return axios
    .get<{ data: IPaginationResponse<IEmployeePayrollResult> }>(`/employee-payroll?payrollRequestId=${oid}`, { params })
    .then(({ data }) => data.data)
}

export const fetchPayrollRequestDetail = (oid: string) => {
  return axios.get<{ data: IEmployeePayrollDetail }>(`/employee-payroll/${oid}`).then(({ data }) => data.data)
}

export const deletePayrollRequestDetail = (oid: string) => {
  return axios.delete(`/employee-payroll/${oid}`)
}

export const updatePayrollComponentDetail = (oid: string, payload: Record<string, any>) => {
  return axios.put(`/employee-payroll/${oid}/component`, payload)
}

export const fetchPayrollMatrixs = (params?: IPaginationParam) => {
  return axios.get<{ data: IPaginationResponse<IPayrollMatrix> }>('/payroll-matrix', { params }).then(({ data }) => data.data)
}

export const fetchPayrollMatrix = (oid: string) => {
  return axios.get<{ data: IPayrollMatrix }>(`/payroll-matrix/${oid}`).then(({ data }) => data.data)
}

export const createPayrollMatrix = (payload: Record<string, any>) => {
  return axios.post<{ data: IPayrollMatrix }>('/payroll-matrix', payload).then(({ data }) => data.data)
}

export const deletePayrollMatrix = (oid: string) => {
  return axios.delete(`/payroll-matrix/${oid}`)
}

/**
 * Payroll Component Policy
 */

export const fetchPayrollComponentPolicy = (params?: { active?: boolean } & IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IPayrollComponentPolicy> }>(`/payroll-policy`, { params, signal })
    .then((response) => response.data.data)
}

export const createPayrollComponentPolicy = (payload: Record<string, any>) => {
  return axios.post<{ data: IPayrollComponentPolicyPayload }>('/payroll-policy', payload).then(({ data }) => data.data)
}

export const deletePayrollComponentPolicy = (oid: string) => {
  return axios.delete(`/payroll-policy/${oid}`)
}

export const createTaxSettings = (payload: Record<string, any>) => {
  return axios.post<{ data: IPayrollTaxSettings }>('/tax-setting', payload).then(({ data }) => data.data)
}

export const updateTaxSettings = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: IPayrollTaxSettings }>(`/tax-setting/${oid}`, payload).then(({ data }) => data.data)
}

export const fetchTaxSettings = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IPayrollTaxSettings> }>('/tax-setting', { params, signal })
    .then((response) => response.data.data)
}

export const fetchTaxSetting = (oid: string) => {
  return axios.get<{ data: IPayrollTaxSettings }>(`/tax-setting/${oid}`).then(({ data }) => data.data)
}

export const deleteTaxSetting = (oid: string) => {
  return axios.delete(`/tax-setting/${oid}`)
}

export const fetchEffectiveRates = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IEffectiveRate> }>('/effective-rate', { params, signal })
    .then((response) => response.data.data)
}

export const deleteEffectiveRate = (oid: string) => {
  return axios.delete(`/effective-rate/${oid}`)
}

export const createEffectiveRate = (payload: Record<string, any>) => {
  return axios.post<{ data: IEffectiveRate }>('/effective-rate', payload).then(({ data }) => data.data)
}

export const fetchEffectiveRate = (oid: string) => {
  return axios.get<{ data: IEffectiveRate }>(`/effective-rate/${oid}`).then(({ data }) => data.data)
}

export const updateEffectiveRate = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: IEffectiveRate }>(`/effective-rate/${oid}`, payload).then(({ data }) => data.data)
}

export const fetchEffectiveRateByYear = (payload: Record<string, any>) => {
  return axios.post<{ data: IEffectiveRate }>('/effective-rate/by-year', payload).then(({ data }) => data.data)
}

/**
 * Payroll Period
 */

export const fetchPayrollPeriods = (params?: IPaginationParam, signal?: GenericAbortSignal) => {
  return axios
    .get<{ data: IPaginationResponse<IPayrollPeriod> }>('/payroll-period', { params, signal })
    .then((response) => response.data.data)
}

export const fetchPayrollPeriod = (oid: string) => {
  return axios.get<{ data: IPayrollPeriod }>(`/payroll-period/${oid}`).then((response) => response.data.data)
}

export const createPayrollPeriods = (payload: Record<string, any>) => {
  return axios.post<{ data: IPayrollPeriod }>(`/payroll-period`, payload).then((response) => response.data.data)
}

export const updatePayrollPeriods = (oid: string, payload: Record<string, any>) => {
  return axios.put<{ data: IPayrollPeriod }>(`/payroll-period/${oid}`, payload).then((response) => response.data.data)
}

export const deletePayrollPeriods = (oid: string) => {
  return axios.delete(`payroll-period/${oid}`).then((response) => response.data.data)
}
